import { useMemo } from 'react';

import { Ati } from '@atlassian/ari';
import {
	CrossAppMenuItem,
	UniversalCreateMenuItem,
	type UniversalCreateMenuSection,
} from '@atlassian/universal-create/types';
import {
	isEnvironmentBlockEnabled,
	isFeatureEnabled,
} from '@atlassiansox/frontend-feature-encoding';

import { useUserHasCapabilities } from '@townsquare/capability-provider/useUserHasCapabilities';
import { useUserHasAgentCreateCapability } from '@townsquare/capability-provider-agg/useUserHasAgentCapabilities';
import { filterUndefined } from '@townsquare/filter-type';
import { useGoalCapabilities } from '@townsquare/goal-capabilities';
import { useUniversalCreateItems as useBaseUniversalCreateItems } from '@townsquare/navigation/universal-create/items';
import { type UseUniversalCreateItemsProps } from '@townsquare/navigation/universal-create/types';
import { useProjectCapabilities } from '@townsquare/project-capabilities';
import { useRovoEntitlement } from '@townsquare/rovo/entitlement';
import { isHomeCrossAppCreateEnabled } from '@townsquare/stat-sig/aem';
import { fg } from '@townsquare/stat-sig/gate';
import { useIsTeamsEnabled } from '@townsquare/teams-app-config/useIsTeamsEnabled';

const CROSS_APP_ATIS = {
	JIRA_ISSUE: 'ati:cloud:jira:issue',
	CONFLUENCE_PAGE: 'ati:cloud:confluence:page',
	CONFLUENCE_WHITEBOARD: 'ati:cloud:confluence:whiteboard',
	LOOM_VIDEO: 'ati:cloud:loom:video',
	TRELLO_BOARD: 'ati:cloud:trello:board',
} as const;

interface UseUniversalCreateItemsOptions {
	onCreateAgent?: () => void;
}

const getCrossAppItems = (): CrossAppMenuItem[] => {
	if (!isHomeCrossAppCreateEnabled()) {
		return [];
	}

	return [
		isFeatureEnabled('jira', 'jira-issue')
			? { ati: Ati.parse(CROSS_APP_ATIS.JIRA_ISSUE), type: 'link' }
			: undefined,
		isEnvironmentBlockEnabled('confluence')
			? { ati: Ati.parse(CROSS_APP_ATIS.CONFLUENCE_PAGE) }
			: undefined,
		isFeatureEnabled('confluence', 'confluence-whiteboards')
			? { ati: Ati.parse(CROSS_APP_ATIS.CONFLUENCE_WHITEBOARD) }
			: undefined,
		isEnvironmentBlockEnabled('loom') ? { ati: Ati.parse(CROSS_APP_ATIS.LOOM_VIDEO) } : undefined,
		isEnvironmentBlockEnabled('trello')
			? { ati: Ati.parse(CROSS_APP_ATIS.TRELLO_BOARD) }
			: undefined,
	].filter(filterUndefined) as CrossAppMenuItem[];
};

export const useUniversalCreateItems = ({
	onCreateAgent,
}: UseUniversalCreateItemsOptions = {}): UseUniversalCreateItemsProps => {
	const { teamCreate, goalCreate, projectCreate, chatCreate, agentCreate } =
		useBaseUniversalCreateItems({ onCreateAgent });
	const userHasGoalsAccess = useUserHasCapabilities(['goals_app:access'], true);
	const userHasProjectsAccess = useUserHasCapabilities(['projects_app:access'], true);
	const projectCapabilities = useProjectCapabilities();
	const canCreateProject = fg('townsquare_m3_group_permissions')
		? projectCapabilities.canCreateProject
		: true;
	const { canCreateGoal } = useGoalCapabilities();
	const canCreateGoalItem = fg('townsquare_m3_group_permissions') ? canCreateGoal : true;
	const { data: rovoEntitlement, loading: isRovoLoading } = useRovoEntitlement();
	const isRovoEnabled = !!rovoEntitlement?.isRovoLLMEnabled;
	const userHasAgentCreateAccess = useUserHasAgentCreateCapability({ skipFetch: !isRovoEnabled });
	const isUserHasAgentCreateAccessLoading = isRovoEnabled && userHasAgentCreateAccess === null;
	const isRovoItemLoading = isUserHasAgentCreateAccessLoading || isRovoLoading;
	const isTeamsEnabled = useIsTeamsEnabled();

	const items = useMemo<UniversalCreateMenuItem[]>(() => {
		const defaultHomeItems = [
			userHasProjectsAccess && canCreateProject ? projectCreate : undefined,
			userHasGoalsAccess && canCreateGoalItem ? goalCreate : undefined,
			isTeamsEnabled ? teamCreate : undefined,
		].filter(filterUndefined) as UniversalCreateMenuItem[];

		const fullscreenChatItems = isRovoEnabled
			? ([chatCreate, userHasAgentCreateAccess ? agentCreate : undefined].filter(
					filterUndefined,
				) as UniversalCreateMenuItem[])
			: [];

		const crossAppItems = getCrossAppItems();

		return [
			...crossAppItems,
			...fullscreenChatItems,
			...defaultHomeItems,
		] as UniversalCreateMenuItem[];
	}, [
		userHasProjectsAccess,
		canCreateProject,
		projectCreate,
		userHasGoalsAccess,
		canCreateGoalItem,
		goalCreate,
		isTeamsEnabled,
		teamCreate,
		isRovoEnabled,
		chatCreate,
		userHasAgentCreateAccess,
		agentCreate,
	]);

	const sections = useMemo<UniversalCreateMenuSection[]>(
		() => [
			{
				type: 'appDefinedSection' as const,
				items,
			},
		],
		[items],
	);

	return {
		items: sections,
		isItemLoading: isRovoItemLoading,
	};
};
